<template>
	<ModalWindow class="email-modal" :show="show" @close="error = false; showRecipients = false; $emit('close');">
		<template #title>
			<template v-if="loading"></template>
			<template v-else-if="error || email === null">Error</template>
			<template v-else><div class="email-subject">{{ email.subject }}</div></template>
		</template>
		<template #body>
			<template v-if="loading">
				<div style="color: #808080; display: flex; justify-content: center;"><span class="fa fa-spinner fa-pulse fa-10x"></span></div>
			</template>
			<template v-else-if="error || email === null">Error loading email.</template>
			<template v-else>
				<div class="info-section">
					<div class="email-info">
						<span class="info-type">Sent At</span>
						<span>{{ formatDateTime(new Date(email.createdAt * 1000)) }}</span>

						<span class="info-type">From</span>
						<span>{{ email.from }}</span>

						<span class="info-type">Recipients</span>
						<span style="display: flex; align-items: start;">
							<span style="flex-grow: 1;">{{ recipientsString }}</span>
							<button type="button" class="btn btn-sm btn-info" @click="showRecipients = true"><span class="fas fa-table"/></button>
						</span>
					</div>
				</div>
				<div v-if="email.attachments.length > 0" class="info-section">
					<div class="attachments">
						<strong class="info-type">Attachments</strong>
						<template v-for="({id, filename}, i) in email.attachments" :key="id">
							<a :href="`/email/${email.id}/attachment/${id}`">
								{{ filename }}
							</a>
							<template v-if="i < email.attachments.length - 1">, </template>
						</template>
					</div>
				</div>
				<div class="body-section"><ShadowDom :html="email.body" class="email-body"/></div>
			</template>
		</template>
	</ModalWindow>

	<ModalWindow
		class="recipients-modal"
		:show="show && showRecipients && email !== null"
		@close="showRecipients = false;"
		backdropZIndex="1070"
		modalZIndex="1075"
	>
		<template #title>Recipients</template>
		<template #body>
			<table class="recipients-table table table-striped">
				<thead>
					<tr>
						<th>Rule</th>
						<th>Group</th>
						<th>Recipient</th>
					</tr>
				</thead>
				<tbody>
					<template v-for="recipientsForRule in email?.recipients" :key="[recipientsForRule.rule?.id, recipientsForRule.group?.id]">
						<tr>
							<td :rowspan="recipientsForRule.recipients.length">
								<template v-if="recipientsForRule.rule !== null">
									<a
										v-if="permissions.editRule"
										target="_blank"
										:href="`/rule/${recipientsForRule.rule.id}/edit?email_id=${email?.id}`"
									>
										{{  recipientsForRule.rule.name }}
									</a>
									<template v-else>{{ recipientsForRule.rule.name }}</template>
								</template>
							</td>
							<td :rowspan="recipientsForRule.recipients.length">
								<template v-if="recipientsForRule.group !== null">
									<a v-if="permissions.editGroup" target="_blank" :href="`/group/${recipientsForRule.group.id}/edit`">
										{{  recipientsForRule.group.name }}
									</a>
									<template v-else>{{ recipientsForRule.group.name }}</template>
								</template>
							</td>
							<td>
								<a v-if="permissions.editRecipient" target="_blank" :href="`/recipient/${recipientsForRule.recipients[0]?.id}/edit`">
									{{  recipientsForRule.recipients[0]?.emailAddress }}
								</a>
								<template v-else>{{ recipientsForRule.recipients[0]?.emailAddress }}</template>
							</td>
						</tr>
						<tr v-for="recipient in recipientsForRule.recipients.slice(1)" :key="recipient.id">
							<td>
								<a v-if="permissions.editRecipient" target="_blank" :href="`/recipient/${recipient.id}/edit`">
									{{  recipient.emailAddress }}
								</a>
								<template v-else>{{ recipient.emailAddress }}</template>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</template>
	</ModalWindow>
</template>

<style scoped lang="scss">
.info-section, .body-section {
	margin: calc(var(--bs-modal-padding) * -1);
	margin-bottom: 0;
	padding: var(--bs-modal-padding);
	border-bottom: 1px solid var(--bs-modal-header-border-color);
	&:not(:first-child) {
		margin-top: 0;
	}

	&:last-child {
		border-bottom: none;
		padding-bottom: 0;
	}
}

.info-section {
	flex-shrink: 0;
}

.body-section {
	display: flex;
	overflow: hidden;
}

.info-type {
	font-weight: bold;
	margin-right: 0.5em;
	&:after {
		content: ":";
	}
}

.email-info {
	display: grid;
	grid-template-columns: max-content 1fr;

	// Scroll if there are more than 2 lines of recipients
	max-height: 6.75em;
	overflow-y: auto;

	.info-type {
		text-align: right;
	}
}

.attachments {
	// Scroll if there are more than 2 lines of attachments
	max-height: 3.75em;
	overflow-y: auto;
}

.email-body {
	overflow: auto;
	width: 100%;
}

.email-subject {
	// Scroll if the subject is more than 2 lines
	max-height: 3.75em;
	overflow-y: auto;
}

.recipients-table {
	tbody tr td:not(:last-of-type) {
		// Disable striping for rowspan columns
		--bs-table-accent-bg: unset;
		--bs-table-striped-color: unset;
	}

	thead th {
		position: sticky;
		top: calc(var(--bs-modal-padding) * -1);
		background: var(--bs-modal-bg);
	}
}

</style>

<style lang="scss">
// Make overflow scrolling work correctly
// This can't be scoped due to the teleport on the modal
.email-modal {
	max-width: 60em;
	display: flex;
	max-height: max(calc(100vh - 3.5rem), 35rem);

	.modal-body {
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}
}

.recipients-modal {
	max-width: 45em;
	display: flex;
	max-height: max(calc(100vh - 3.5rem), 20rem);

	.modal-body {
		display: flex;
		flex-direction: column;
		overflow: auto;
	}
}
</style>

<script lang="ts">
import { formatDateTime, isObject } from "common";
import { defineComponent } from "vue";
import ModalWindow from "./ModalWindow.vue";
import ShadowDom from "./ShadowDom.vue";

interface RecipientsForRule {
	rule: {id: number; name: string}|null;
	group: {id: number; name: string}|null;
	recipients: Array<{id: number; emailAddress: string}>;
}
type Recipients = Array<RecipientsForRule>;

interface Email {
	id: number;
	from: string;
	subject: string;
	recipients: Recipients;
	body: string;
	attachments: Array<{ id: number; filename: string }>;
	createdAt: number;
}

interface Permissions {
	editRule: boolean;
	editGroup: boolean;
	editRecipient: boolean;
}

declare const permissions: Permissions;

const component = defineComponent({
	components: {
		ModalWindow,
		ShadowDom,
	},
	props: {
		show: { type: Boolean, required: true },
		id: { type: Number, required: true },
	},
	data() {
		return {
			email: null as Email|null,
			loading: false,
			error: false,

			showRecipients: false,

			permissions,
		};
	},
	emits: [
		"close",
	],
	computed: {
		recipientsString(): string {
			if (this.email === null) {
				return "";
			}

			const recipients = new Set();
			for (const recipientsForRule of this.email.recipients) {
				for (const recipient of recipientsForRule.recipients) {
					recipients.add(recipient.emailAddress);
				}
			}

			return [...recipients].sort().join(", ");
		},
	},
	watch: {
		id: {
			handler: async function(id: number): Promise<void> {
				try {
					this.loading = true;
					const response = await fetch(`/email/${id}`, {
						method: "GET",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
						},
					});

					// Make sure the user hasn't tried to open another email while this one was still loading
					if (id === this.id) {
						try {
							const data: unknown = await response.json();
							if (!isObject(data) || typeof data.id !== "number") {
								throw data;
							}
							this.email = data as any as Email; // eslint-disable-line @typescript-eslint/no-explicit-any
						} finally {
							this.loading = false;
						}
					}
				} catch (error) {
					this.error = true;
				}
			},
			immediate: true,
		},
	},
	methods: {
		formatDateTime,
	},
});

export default component;
</script>
